import React, { useState } from 'react';
import { X, Plus } from 'lucide-react';

interface SummarySettingsModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (settings: SummarySettings) => void;
  contentType?: 'document' | 'media';
  totalPages?: number;
  totalDuration?: string;
}

export interface SummarySettings {
  template: string;
  ranges: { start: string; end: string }[];
}

const TEMPLATES = [
  { id: 'executive', label: 'Executive Summary', description: 'High-level overview with key takeaways and insights' },
  { id: 'detailed', label: 'Detailed Summary', description: 'Comprehensive summary with key points and context' },
  { id: 'quick', label: 'Quick Summary', description: 'Brief overview with essential information only' }
];

export const SummarySettingsModal: React.FC<SummarySettingsModalProps> = ({
  open,
  onClose,
  onSubmit,
  contentType = 'media',
  totalPages,
  totalDuration = '00:04:06'
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState('Executive Summary');
  const [ranges, setRanges] = useState([
    contentType === 'document' 
      ? { start: '1', end: String(totalPages || 10) }
      : { start: '00:00:00', end: totalDuration }
  ]);

  const isDocument = contentType === 'document';

  if (!open) return null;

  const addRange = () => {
    setRanges([...ranges, isDocument ? { start: '1', end: '1' } : { start: '00:00:00', end: '00:00:00' }]);
  };

  const updateRange = (index: number, field: 'start' | 'end', value: string) => {
    const newRanges = [...ranges];
    newRanges[index][field] = value;
    setRanges(newRanges);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      template: selectedTemplate,
      ranges
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-x-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in-0"
        onClick={onClose}
      />
      
      {/* Dialog Content */}
      <section 
        role="dialog"
        aria-labelledby="summary-dialog-title"
        aria-describedby="summary-dialog-description"
        className="relative z-50 mx-1 my-1 sm:mx-6 sm:my-16 flex flex-col w-full max-w-lg rounded-xl border border-border bg-background py-1 shadow-lg overflow-y-hidden"
      >
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-2 top-2 z-10 rounded-full p-1 text-muted-foreground hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>

        {/* Header */}
        <header className="px-6 py-4 pb-0 mb-4 flex flex-col space-y-0.5 text-center sm:text-left" id="summary-dialog-title">
          <div className="text-lg font-normal text-foreground">Customize Summary</div>
          <div className="text-sm font-normal text-muted-foreground">
            Select specific prompts and ranges for your summary set
          </div>
        </header>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="flex flex-1 flex-col gap-3 px-6 py-2 pt-0" id="summary-dialog-description">
            <div className="space-y-6">
              {/* Templates */}
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex flex-col gap-2">
                    <span className="text-sm font-medium text-foreground">Templates</span>
                    
                    {TEMPLATES.map((template) => (
                      <div
                        key={template.id}
                        onClick={() => setSelectedTemplate(template.label)}
                        className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${
                          selectedTemplate === template.label
                            ? 'bg-[hsl(var(--selected-subtle))] border-[hsl(var(--selected-border))]'
                            : 'border-border hover:bg-accent'
                        }`}
                      >
                        <div className="flex h-4 w-4 items-center justify-center rounded-full border-2 border-muted-foreground">
                          {selectedTemplate === template.label && (
                            <div className="h-2 w-2 rounded-full bg-foreground"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <span className="text-sm font-medium text-foreground">{template.label}</span>
                          <p className="mt-1 text-xs text-muted-foreground">{template.description}</p>
                        </div>
                      </div>
                    ))}

                    <div className="flex cursor-pointer items-center gap-3 rounded-xl border border-border p-3 transition-colors bg-background hover:bg-accent">
                      <div className="flex h-4 w-4 items-center justify-center rounded-full border-2 border-muted-foreground"></div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-foreground">Add Custom Prompt</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Summary Range */}
              <div className="space-y-4">
                <label className="text-sm font-medium leading-none text-foreground">
                  Summary Range {isDocument ? '(Pages)' : '(Time)'}
                </label>
                <div className="space-y-4">
                  {ranges.map((range, index) => (
                    <div key={index} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium leading-none text-foreground">
                          Range {index + 1}
                        </label>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex-1">
                          <label className="text-sm font-medium leading-none text-muted-foreground">
                            {isDocument ? 'Start Page' : 'Start'}
                          </label>
                          <input
                            type={isDocument ? 'number' : 'text'}
                            placeholder={isDocument ? '1' : '00:00:00'}
                            min={isDocument ? 1 : undefined}
                            value={range.start}
                            onChange={(e) => updateRange(index, 'start', e.target.value)}
                            className="mt-1 flex h-10 w-full rounded-lg border border-input bg-background px-4 text-sm transition-all duration-200 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring text-foreground"
                          />
                        </div>
                        <span className="mt-6 text-muted-foreground">to</span>
                        <div className="flex-1">
                          <label className="text-sm font-medium leading-none text-muted-foreground">
                            {isDocument ? 'End Page' : 'End'}
                          </label>
                          <input
                            type={isDocument ? 'number' : 'text'}
                            placeholder={isDocument ? '10' : '00:00:00'}
                            min={isDocument ? 1 : undefined}
                            value={range.end}
                            onChange={(e) => updateRange(index, 'end', e.target.value)}
                            className="mt-1 flex h-10 w-full rounded-lg border border-input bg-background px-4 text-sm transition-all duration-200 placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring text-foreground"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <button
                    type="button"
                    onClick={addRange}
                    className="inline-flex w-full items-center justify-center whitespace-nowrap text-sm font-medium transition-colors border border-input bg-background hover:bg-accent text-foreground h-9 rounded-lg px-3"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Another Range
                  </button>
                  <div className="text-xs text-muted-foreground">
                    {isDocument 
                      ? `Maximum range: Page 1 to ${totalPages || 'last page'}`
                      : `Maximum range: 00:00 to ${totalDuration}`
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="flex flex-row gap-2 px-6 py-4 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors bg-secondary text-secondary-foreground hover:bg-accent h-10 px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors bg-primary text-primary-foreground hover:bg-primary/90 h-10 px-4 py-2"
            >
              Generate Summary
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
};
